import type { ColumnFiltersState, SortingState } from '@tanstack/react-table'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useApiQuery } from './use-api'
import { useDebounce } from './use-debounce'

type TableDataConfig = {
  endpoint: string
  queryKey: string
  initialPageSize?: number
  initialFilters?: ColumnFiltersState
  searchFields?: string | string[]
  debounceMs?: {
    filters?: number
    search?: number
    sorting?: number
  }
  fieldMappings?: {
    filters?: (field: string, value: unknown) => { field: string; value: unknown } | null
    sorting?: (field: string) => string
  }
}

/**
 * Generic hook for table data with pagination, filtering, sorting, and search
 */
export function useTableData<TData>(config: TableDataConfig) {
  const {
    endpoint,
    queryKey,
    debounceMs = { filters: 500, search: 500, sorting: 300 },
    fieldMappings,
    initialPageSize = 20,
    initialFilters = [],
    searchFields = 'name',
  } = config

  // Pagination state
  const [queryParams, setQueryParams] = useState({
    page: 1,
    per_page: initialPageSize,
  })

  // Local UI state
  const [localFilters, setLocalFilters] = useState<ColumnFiltersState>(initialFilters)
  const [localSearch, setLocalSearch] = useState<string>('')
  const [localSorting, setLocalSorting] = useState<SortingState>([])

  // Track if local state has changed but debounced values haven't updated yet
  const [hasPendingChanges, setHasPendingChanges] = useState(false)

  // Debounced values for API calls
  const debouncedFilters = useDebounce(localFilters, debounceMs.filters ?? 500)
  const debouncedSearch = useDebounce(localSearch, debounceMs.search ?? 500)
  const debouncedSorting = useDebounce(localSorting, debounceMs.sorting ?? 300)

  // Track when debounced values catch up with local state
  useEffect(() => {
    const filtersMatch = JSON.stringify(localFilters) === JSON.stringify(debouncedFilters)
    const searchMatch = localSearch === debouncedSearch
    const sortingMatch = JSON.stringify(localSorting) === JSON.stringify(debouncedSorting)

    setHasPendingChanges(!(filtersMatch && searchMatch && sortingMatch))
  }, [localFilters, debouncedFilters, localSearch, debouncedSearch, localSorting, debouncedSorting])

  // Build API parameters
  const apiQueryParams = useMemo(() => {
    const params: Record<string, unknown> = {
      page: queryParams.page,
      per_page: queryParams.per_page,
    }

    // Add search if provided
    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim()
      // Add search_fields parameter to specify which fields to search in
      params.search_fields = Array.isArray(searchFields) ? searchFields.join(',') : searchFields
    }

    // Add sorting if provided
    if (debouncedSorting.length > 0) {
      const sortArray = debouncedSorting.map((sort) => {
        const field = fieldMappings?.sorting ? fieldMappings.sorting(sort.id) : sort.id
        return {
          field,
          direction: sort.desc ? 'desc' : 'asc',
        }
      })
      params.sort = JSON.stringify(sortArray)
    }

    // Add filters if provided
    if (debouncedFilters.length > 0) {
      const filterGroups = debouncedFilters
        .map((filter) => {
          const conditions = []

          // Apply field mapping if provided
          const mappedField = fieldMappings?.filters
            ? fieldMappings.filters(filter.id, filter.value)
            : { field: filter.id, value: filter.value }

          if (!mappedField) return { conditions: [] }

          // Handle different filter value types
          if (typeof mappedField.value === 'object' && mappedField.value !== null) {
            // Handle range filters (number/date)
            if ('min' in mappedField.value && mappedField.value.min) {
              conditions.push({
                field: mappedField.field,
                operator: 'gte' as const,
                value: mappedField.value.min,
              })
            }
            if ('max' in mappedField.value && mappedField.value.max) {
              conditions.push({
                field: mappedField.field,
                operator: 'lte' as const,
                value: mappedField.value.max,
              })
            }
            if ('from' in mappedField.value && mappedField.value.from) {
              conditions.push({
                field: mappedField.field,
                operator: 'gte' as const,
                value: mappedField.value.from,
              })
            }
            if ('to' in mappedField.value && mappedField.value.to) {
              conditions.push({
                field: mappedField.field,
                operator: 'lte' as const,
                value: mappedField.value.to,
              })
            }
          } else if (mappedField.value !== undefined && mappedField.value !== '') {
            // Handle simple filters (text/select)
            if (mappedField.value === 'not_null') {
              // Special case for not_null operator
              conditions.push({
                field: mappedField.field,
                operator: 'is_not_null' as const,
              })
            } else if (mappedField.value === null) {
              // Special case for null value
              conditions.push({
                field: mappedField.field,
                operator: 'is_null' as const,
              })
            } else {
              // Regular filter - use custom operator if provided, otherwise default to 'eq'
              const operator = 'operator' in mappedField ? mappedField.operator : 'eq'
              conditions.push({
                field: mappedField.field,
                operator: operator as
                  | 'eq'
                  | 'like'
                  | 'ilike'
                  | 'ne'
                  | 'in'
                  | 'nin'
                  | 'gte'
                  | 'lte'
                  | 'gt'
                  | 'lt'
                  | 'is_null'
                  | 'is_not_null',
                value: mappedField.value,
              })
            }
          }

          return { conditions }
        })
        .filter((group) => group.conditions.length > 0)

      if (filterGroups.length > 0) {
        params.filters = JSON.stringify({
          groups: filterGroups,
        })
      }
    }

    // Debug log for API parameters
    console.debug('🔍 API Query Params:', params)

    return params
  }, [
    queryParams,
    debouncedFilters,
    debouncedSearch,
    debouncedSorting,
    fieldMappings,
    searchFields,
  ])

  // API call
  const queryKeyArray = useMemo(
    () => [queryKey, JSON.stringify(apiQueryParams)],
    [queryKey, apiQueryParams],
  )
  const {
    data: response,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useApiQuery<TData>(queryKeyArray, endpoint, { params: apiQueryParams })

  // Data is already unwrapped by useApiQuery
  const data = response

  // Event handlers
  const handlePaginationChange = useCallback(
    (
      updater:
        | ((old: { pageIndex: number; pageSize: number }) => {
            pageIndex: number
            pageSize: number
          })
        | { pageIndex: number; pageSize: number },
    ) => {
      const newPagination =
        typeof updater === 'function'
          ? updater({ pageIndex: queryParams.page - 1, pageSize: queryParams.per_page })
          : updater

      setQueryParams((prev) => ({
        ...prev,
        page: newPagination.pageIndex + 1,
        per_page: newPagination.pageSize,
      }))
    },
    [queryParams.page, queryParams.per_page],
  )

  const handleFiltersChange = useCallback((filters: ColumnFiltersState) => {
    setLocalFilters(filters)
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
    }))
  }, [])

  const handleSortChange = useCallback((sorting: SortingState) => {
    setLocalSorting(sorting)
  }, [])

  const handleSearchChange = useCallback((searchValue: string) => {
    setLocalSearch(searchValue)
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
    }))
  }, [])

  return {
    // Data
    data,
    isLoading: isLoading || hasPendingChanges,
    isFetching,
    error,
    refetch,

    // Pagination
    paginationState: {
      pageIndex: queryParams.page - 1,
      pageSize: queryParams.per_page,
    },
    onPaginationChange: handlePaginationChange,

    // Filters
    filters: localFilters,
    onFiltersChange: handleFiltersChange,

    // Sorting
    sorting: localSorting,
    onSortingChange: handleSortChange,

    // Search
    searchValue: localSearch,
    onSearchChange: handleSearchChange,
  }
}
